import useHttpProvider from "@/common/hooks/use-http";
import { AuthenticationProvider } from "@/common/providers/authentication";
import {
	Carousel,
	CarouselContent,
	CarouselItem,
	type CarouselApi,
} from "@/components/ui/carousel";
import useEmblaCarousel from "embla-carousel-react";
import SEO from "@/common/components/seo";
import FadeIn from "@/common/components/fade-in";
import ArrowRightIcon from "@/common/svgs/arrow-right";
import GetStartedIcon from "@/common/svgs/get-started";
import HowItWorksWeb from "@/common/svgs/how-it-works-web";
import SaveIcon from "@/common/svgs/save";
import SignUpIcon from "@/common/svgs/sign-up";
import SupportIcon from "@/common/svgs/support";
import WinIcon from "@/common/svgs/win";
import { Button } from "@/components/ui/button";
import { SignUpRoute } from "@/features/authentication/route";
import { Promotion } from "@/features/giveaways/models/giveaway";
import * as GiveawaysService from "@/features/giveaways/services/giveaways";
import PlanSelection from "@/features/payments/containers/plans";
import { Plan } from "@/features/payments/models/plan";
import useManageMembership from "@/features/profile/hooks/use-manage-membership";
import { ProfileManageMembershipRoute } from "@/features/profile/route";
import { Winner } from "@/features/winners/models/winner";
import * as WinnersService from "@/features/winners/services/winners";
import { cn, formatShortNameWithState } from "@/lib/utils";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import OurPartners from "../../partners/containers/partners";
import { AboutUsRoute, GiveawaysRoute, WinnersRoute } from "../route";

function Header() {
	return (
		<div className="bg-blue relative min-h-[calc(100vh+20px)] overflow-hidden">
			<div className="[clip-path:url(#wave-clip)]">
				<img
					src={"images/home-page-header.png"}
					className="absolute h-max w-max"
				/>
				<div className="h-max">
					<div className="absolute top-1/2 left-1/2 flex -translate-x-1/2 -translate-y-1/2 transform flex-col gap-8 text-center">
						<FadeIn direction="up" delay={0.2}>
							<p className="web-heading-h1 text-white">
								Dive Into Rewards For Fishing Enthusiasts
							</p>
						</FadeIn>
						<FadeIn direction="up" delay={0.4}>
							<p className="web-body-b2 text-white">
								Spending too much on fishing? We've got you covered! Join our
								community for exclusive industry-wide discounts and weekly
								giveaways.
							</p>
						</FadeIn>
					</div>

					<div className="absolute bottom-12 left-1/2 flex -translate-x-1/2 transform flex-col items-center gap-6">
						{/* <p className="web-heading-h4 text-white">Get started</p> */}
						{/* TODO: Need to see why this isn't working */}
						<FadeIn direction="up" delay={0.6}>
							<GetStartedIcon />
						</FadeIn>
					</div>
				</div>

				<video
					src="videos/home-page-background.mp4"
					className="min-h-[calc(100vh+20px)] w-screen object-cover"
					autoPlay
					loop
					muted
					playsInline
					preload="metadata"
				/>
			</div>
		</div>
	);
}

function UnitingFishingEnthousiast() {
	const navigate = useNavigate();

	return (
		<div
			className="relative flex flex-col justify-between gap-44 px-4 py-12 md:flex-row md:px-37 md:py-32"
			style={{
				background:
					"radial-gradient(277.81% 189.47% at 80.08% -45.37%, #2EDBCA 1.3%, #004096 43.8%, #470096 100%)",
			}}
		>
			<img
				src={"icons/star.png"}
				className="absolute top-2 left-2 h-12 w-12 -rotate-12 transform md:top-13 md:left-13 md:h-27 md:w-27"
			/>
			<div className="flex flex-col gap-12">
				<div className="flex flex-col gap-8">
					<span className="web-heading-h2 text-white">
						Uniting Fishing Enthusiast{" "}
						<span className="text-green-300">
							Across <span className="underline">Australia</span>
						</span>
					</span>
					<div className="web-body-b1 text-white">
						<p className="web-body-b1 text-white">
							Fishing is more than a hobby—it's a way of life. Our mission is to
							connect anglers across Australia, providing a platform that grants
							access to the best industry-wide discounts, helping you save
							hundreds of dollars so you can get back to what you love—fishing.
						</p>
						<br />
						<p className="web-body-b1 text-base text-white">
							We offer exclusive
							<img
								src={"images/united-fishermen.jpeg"}
								className="float-right mx-2 inline-block h-31 w-34 rounded-[42px] object-cover align-middle md:hidden"
							/>
							opportunities, from partner discounts to epic giveaways, ensuring
							our patrons get the most out of their membership. Whether you're a
							seasoned angler or just starting out, our community welcomes you
							with open arms and invites you to share in the rewards.
						</p>
						<br />
						<p className="web-body-b1 text-white">
							Dive in, explore new waters, and turn every cast into an
							unforgettable adventure. Together, we’ll make fishing even more
							rewarding!
						</p>
					</div>
				</div>
				<Button
					className="flex w-50 flex-row gap-2"
					onClick={() => navigate(AboutUsRoute)}
				>
					Find out more <ArrowRightIcon white />
				</Button>
			</div>

			<img
				src={"images/united-fishermen.jpeg"}
				className="h-150 w-150 rounded-[72px] object-cover max-sm:hidden"
			/>
		</div>
	);
}

function GiveawayColumn({
	images,
	intervalMs,
	startIndex,
	pausedRef,
}: {
	images: string[];
	intervalMs: number;
	startIndex?: number;
	pausedRef: React.MutableRefObject<boolean>;
}) {
	const [emblaRef, emblaApi] = useEmblaCarousel({
		loop: true,
		axis: "y",
		startIndex: startIndex ?? 0,
	});

	useEffect(() => {
		if (!emblaApi) return;
		const interval = setInterval(() => {
			if (!pausedRef.current) emblaApi.scrollNext();
		}, intervalMs);
		return () => clearInterval(interval);
	}, [emblaApi, intervalMs, pausedRef]);

	return (
		<div ref={emblaRef} className="h-full flex-1 overflow-hidden">
			<div className="flex h-full flex-col">
				{images.map((src, i) => (
					<div
						key={i}
						className="h-52 w-full flex-shrink-0 pt-3 md:h-56"
					>
						<img
							src={src}
							draggable={false}
							className="pointer-events-none h-full w-full rounded-[20px] bg-white object-cover shadow-sm"
						/>
					</div>
				))}
			</div>
		</div>
	);
}

function GiveawaysImageCarousel({ promotions }: { promotions: Promotion[] }) {
	const baseURL = import.meta.env.VITE_SERVER_API_URL;
	const paused = useRef(false);

	const images = promotions
		.filter((p) => p.prize?.images?.length > 0)
		.flatMap((p) =>
			p.prize.images.map((img) => `${baseURL}/common/image/${img.url}`)
		);

	if (images.length === 0) {
		return (
			<img
				src="images/giveaways.png"
				className="h-80 w-full flex-shrink-0 rounded-[32px] object-cover md:h-[520px] md:w-[440px]"
			/>
		);
	}

	if (images.length === 1) {
		return (
			<img
				src={images[0]}
				className="h-80 w-full flex-shrink-0 rounded-[32px] object-cover md:h-[520px] md:w-[440px]"
			/>
		);
	}

	const col1 = images.filter((_, i) => i % 2 === 0);
	const col2 = images.filter((_, i) => i % 2 === 1);

	return (
		<div
			className="flex h-80 w-full flex-shrink-0 gap-3 overflow-hidden md:h-[520px] md:w-[440px]"
			onMouseEnter={() => (paused.current = true)}
			onMouseLeave={() => (paused.current = false)}
		>
			<GiveawayColumn images={col1} intervalMs={2500} pausedRef={paused} />
			<div className="flex h-full flex-1" style={{ marginTop: "-48px" }}>
				<GiveawayColumn
					images={col2}
					intervalMs={3000}
					startIndex={1}
					pausedRef={paused}
				/>
			</div>
		</div>
	);
}

function RegularEpicGiveAways() {
	const navigate = useNavigate();
	const { http } = useHttpProvider();
	const [allPromotions, setAllPromotions] = useState<Promotion[]>([]);

	useEffect(() => {
		Promise.allSettled([
			GiveawaysService.getActivePromotions(http),
			GiveawaysService.getUpcomingPromotions(http),
			WinnersService.getPreviousWinners(http).then((winners) =>
				winners.map((w) => w.promotion)
			),
		]).then((results) => {
			const combined = results
				.filter((r) => r.status === "fulfilled")
				.flatMap((r) => (r as PromiseFulfilledResult<Promotion[]>).value);

			// Deduplicate by prize id
			const seen = new Set<number>();
			const unique = combined.filter((p) => {
				if (!p?.prize?.id || seen.has(p.prize.id)) return false;
				seen.add(p.prize.id);
				return true;
			});
			setAllPromotions(unique);
		});
	}, []);

	return (
		<div className="bg-blue flex flex-col justify-between gap-12 px-5 py-12 md:flex-row md:gap-28 md:px-37 md:py-24">
			<GiveawaysImageCarousel promotions={allPromotions} />
			<div className="flex flex-col gap-12">
				<div className="flex flex-col gap-8">
					<span className="web-heading-h2">
						<span
							style={{
								background:
									"linear-gradient(186deg, #0FF1E5 -14.52%, #0DAAEC 11.49%, #0823F8 51.18%, #000D74 107.31%)",
								WebkitBackgroundClip: "text",
								backgroundClip: "text",
								WebkitTextFillColor: "transparent",
							}}
						>
							Regular
						</span>{" "}
						Epic GiveAways
					</span>

					<p className="web-body-b1 text-gray-700">
						As a member of our exclusive community, you’ll have the chance to
						reel in incredible prizes every week. From premium fishing gear and
						accessories to once-in-a-lifetime fishing trips sourced from our
						valuable partners, we’re here to make your passion even more
						rewarding.
						<br />
						<br />
						Don’t let these opportunities slip away—join today and turn your
						love for fishing into unforgettable rewards!
					</p>
				</div>

				<Button
					className="flex flex-row gap-2 max-sm:mb-15 max-sm:justify-center md:w-40"
					onClick={() => navigate(GiveawaysRoute)}
				>
					Get started <ArrowRightIcon white />
				</Button>
			</div>
		</div>
	);
}

function HowItWorksStep({
	className,
	title,
	description,
	icon,
}: {
	className?: string;
	title: string;
	description: string;
	icon: React.ReactNode;
}) {
	return (
		<div className={cn("flex flex-col gap-10 md:px-5 md:py-4", className)}>
			{icon}
			<div className="flex flex-col gap-8">
				<p className="web-heading-h4 text-white">{title}</p>
				<p className="web-body-b1 text-white">{description}</p>
			</div>
		</div>
	);
}

function HowItWorks() {
	return (
		<div className="md:px-8">
			<div
				className="relative flex flex-col gap-16 px-4 py-10 md:rounded-[64px] md:px-29 md:py-32"
				style={{
					background:
						"radial-gradient(277.81% 189.47% at 80.08% -45.37%, #2EDBCA 1.3%, #004096 43.8%, #470096 100%)",
				}}
			>
				<img
					src={"icons/gift.png"}
					className="absolute -top-10 left-7/10 h-25 w-22 md:h-57 md:w-50"
				/>

				<img
					src={"icons/star.png"}
					className="absolute top-50 left-17/20 h-9 w-9 -rotate-12 transform md:h-20 md:w-20"
				/>

				<img
					src={"icons/star.png"}
					className="absolute bottom-8 left-17/20 h-27 w-27 transform max-sm:hidden"
				/>

				<p className="web-heading-h2 text-white max-sm:hidden">Win in 4 Simple Steps</p>
				<span className="web-heading-h2 text-white md:hidden">
					How the <span className="text-green-300">Prize Draws</span> Work
				</span>

				<div className="relative grid grid-cols-2 flex-row justify-around gap-8 max-sm:px-4 md:grid-cols-4 md:gap-24">
					<HowItWorksWeb className="max-sm:hidden md:absolute" />

					<HowItWorksStep
						icon={<SignUpIcon />}
						title="Sign Up"
						description="Pick a membership tier and get instant access to the full Reel Rewards experience"
					/>

					<HowItWorksStep
						icon={<SaveIcon />}
						title="Save Big"
						description="Unlock exclusive partner discounts across the industry—put real dollars back in your pocket every time you fish"
					/>

					<HowItWorksStep
						icon={<WinIcon />}
						title="Win Prizes"
						description="Every membership automatically enters you into weekly and monthly giveaways with epic fishing prizes"
					/>

					<HowItWorksStep
						icon={<SupportIcon />}
						title="Refer & Earn"
						description="Invite your mates and score bonus entries for every angler who joins through you—more referrals, bigger chances to win"
					/>
				</div>
			</div>
		</div>
	);
}

function ChooseYourPlan() {
	const navigate = useNavigate();
	const isAuthenticated = AuthenticationProvider.isAuthenticated;

	const { currentSubscription, currentPlan } = useManageMembership();

	const onPlanSelected = (plan: Plan) => {
		if (!isAuthenticated) {
			navigate(SignUpRoute, { state: { plan } });
		} else {
			navigate(ProfileManageMembershipRoute, { state: { plan } });
		}
	};

	const yourPlanStyle = {
		background:
			"linear-gradient(161deg, #0FF1E5 3.9%, #0DAAEC 33.24%, #0823F8 72.3%, #000D74 116.02%)",
		WebkitBackgroundClip: "text",
		backgroundClip: "text",
		WebkitTextFillColor: "transparent",
	};

	return (
		<PlanSelection
			header={
				<div className="flex flex-col gap-6 text-center">
					<span className="web-heading-h2">
						Choose <span style={yourPlanStyle}>Your Plan</span>
					</span>
					<p className="web-body-b1 text-gray-700">
						Choose from 3 tiers for the greatest member membership rewards
					</p>
				</div>
			}
			className="px-5 py-8 md:px-37 md:py-24"
			currentSubscription={currentSubscription}
			currentPlan={currentPlan}
			onPlanSelected={onPlanSelected}
		/>
	);
}

function LuckyAngler({ winner }: { winner: Winner }) {
	const baseURL = import.meta.env.VITE_SERVER_API_URL;
	const prizeImage = `${baseURL}/common/image/${winner.promotion?.prize?.images[0].url}`;

	return (
		<div className="flex flex-col gap-4 max-sm:items-center max-sm:text-center md:gap-8">
			<div
				className="aspect-square w-full rounded-[32px] bg-cover bg-center md:h-82 md:w-auto"
				style={{ backgroundImage: `url('${prizeImage}')` }}
			/>
			<div className="flex flex-col gap-1">
				<p className="web-heading-h4">
					{formatShortNameWithState(
						winner.winner.fullName,
						winner.winner.stateName
					)}
				</p>
				<p className="web-body-b1 text-gray-700">
					{winner.promotion.prize.title}
				</p>
				<p className="web-body-b1 text-gray-700">
					{winner.promotion.endDate.toLocaleString()}
				</p>
			</div>
		</div>
	);
}

function LuckyAnglers() {
	const { http } = useHttpProvider();
	const navigate = useNavigate();
	const [previousWinners, setPreviousWinners] = useState<Winner[]>([]);
	const [anglerApi, setAnglerApi] = useState<CarouselApi>();
	const anglerPaused = useRef(false);

	useEffect(() => {
		WinnersService.getPreviousWinners(http)
			.then(setPreviousWinners)
			.catch(() => {});
	}, []);

	useEffect(() => {
		if (!anglerApi) return;
		const interval = setInterval(() => {
			if (!anglerPaused.current) anglerApi.scrollNext();
		}, 3000);
		return () => clearInterval(interval);
	}, [anglerApi]);

	return (
		<div className="max-sm:bg-light flex flex-col items-center gap-8 px-5 py-8 pr-8 md:flex-row md:py-24 md:pl-37">
			<div className="flex max-w-130 flex-col gap-12">
				<div className="flex flex-col gap-8">
					<span className="web-heading-h2">
						<span
							style={{
								background:
									"linear-gradient(161deg, #0FF1E5 3.9%, #0DAAEC 33.24%, #0823F8 72.3%, #000D74 116.02%)",
								WebkitBackgroundClip: "text",
								backgroundClip: "text",
								WebkitTextFillColor: "transparent",
							}}
						>
							Lucky
						</span>{" "}
						Anglers!
					</span>
					<span className="web-body-b1 text-gray-700">
						Our community members have already landed incredible rewards! From
						state-of-the-art fishing gear to unforgettable trips, these lucky
						anglers have turned their passion into real prizes.
					</span>
				</div>
				<Button
					onClick={() => navigate(WinnersRoute)}
					className="w-58 max-sm:hidden"
				>
					View All
				</Button>
			</div>

			<Carousel
				setApi={setAnglerApi}
				opts={{ loop: true }}
				className="w-full min-w-0 flex-1"
				onMouseEnter={() => (anglerPaused.current = true)}
				onMouseLeave={() => (anglerPaused.current = false)}
			>
				<CarouselContent>
					{previousWinners.map((winner) => (
						<CarouselItem
							key={winner.id}
							className="basis-full sm:basis-1/2 md:basis-1/3"
						>
							<LuckyAngler winner={winner} />
						</CarouselItem>
					))}
				</CarouselContent>
			</Carousel>

			<Button
				className="w-full md:hidden"
				onClick={() => navigate(WinnersRoute)}
			>
				View All
			</Button>
		</div>
	);
}

export default function HomePage() {
	return (
		<div>
			<SEO
				title="Reel Rewards - Win Amazing Prizes"
				description="Join Reel Rewards to win exclusive fishing gear, trips, and more. Connect with anglers across Australia and enjoy industry-wide discounts."
			/>
			<Header />
			<div>
				<FadeIn>
					<OurPartners className="py-8 md:py-16" />
				</FadeIn>
				<FadeIn>
					<UnitingFishingEnthousiast />
				</FadeIn>
				<FadeIn>
					<RegularEpicGiveAways />
				</FadeIn>
				<FadeIn>
					<HowItWorks />
				</FadeIn>
				<FadeIn>
					<ChooseYourPlan />
				</FadeIn>
				<FadeIn>
					<LuckyAnglers />
				</FadeIn>
			</div>
		</div>
	);
}
